import { ReactNode, useMemo } from 'react'
import { useTheme } from 'styled-components'

import { FormattedMessage, useIntl } from 'react-intl'
import { LuCircleCheckBig } from 'react-icons/lu'

import { Button } from '@/lib/core/components/Button'
import { SimpleModal, SimpleModalProps } from '@/lib/core/components/Modal'
import { Table, TableColumns, Td, Tr } from '@/lib/core/components/Table'

import { BridgeId } from '@/lib/features/Assets/interfaces'

import messages from './SharesSummaryModal.messages'
import { Container, SubTitle, TableWrapper } from './SharesSummaryModal.styles'

type Shares = {
  assetId: BridgeId
  description: string
  reference: string
  dates: string
  days: number
  cost: string
}

export type SharesSummaryModalProps = Pick<
  SimpleModalProps,
  'title' | 'headingIcon' | 'isActive'
> & {
  isLoading?: boolean
  onClose?: () => void
  shares: Shares[]
  subtitle?: string | ReactNode
  totalCost?: { [key: string]: number }
}

export const SharesSummaryModal = ({
  headingIcon,
  isActive,
  isLoading,
  onClose = () => null,
  shares,
  subtitle,
  title,
}: SharesSummaryModalProps) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()

  const columns = useMemo<TableColumns>(
    () => [
      {
        accessor: 'assetId',
        label: formatMessage(messages.assetId),
      },
      {
        accessor: 'description',
        label: formatMessage(messages.description),
      },
      {
        accessor: 'reference',
        label: formatMessage(messages.reference),
      },
      {
        accessor: 'dates',
        label: formatMessage(messages.dates),
      },
      {
        accessor: 'days',
        label: formatMessage(messages.days),
      },
    ],
    [],
  )

  return (
    <SimpleModal
      isActive={isActive}
      actionsAlignItems="flex-start"
      headingIcon={headingIcon || <LuCircleCheckBig size="48" color={colors.featureShares} />}
      title={title}
      actions={
        <Button $fitToContent minWidth size="large" variant="secondary" onClick={onClose}>
          <FormattedMessage {...messages.done} />
        </Button>
      }
    >
      <Container>
        <SubTitle>{subtitle}</SubTitle>
        <TableWrapper>
          <Table isLoading={isLoading} columns={columns} hideHeader stickyLoader={false}>
            {shares.map(({ assetId, dates, days, description, reference }) => (
              <Tr key={`${assetId}-${dates}`}>
                <Td>{assetId}</Td>
                <Td>{description}</Td>
                <Td>{reference}</Td>
                <Td>{dates}</Td>
                <Td>{days}</Td>
              </Tr>
            ))}
          </Table>
        </TableWrapper>
      </Container>
    </SimpleModal>
  )
}
